import { useState, useEffect } from 'react';
import { useOrgTimezone, formatDateInTz } from '../../utils/timezone';
import { useAuth } from '../../context/AuthContext';
import { Package, Plus, ArrowDownCircle, Filter, ClipboardCheck } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import SprayNav from '../../components/spray/SprayNav';
import { Tabs } from '../../components/ui/Tabs';
import Button from '../../components/ui/Button';
import { InventoryStockTable } from '../../components/spray';
import sprayService from '../../services/spray';
import type { ChemicalInventoryLocation, InventoryTransaction, InventoryStockLevel } from '../../types/spray';
import { toNumOr } from '@/utils/numeric';

// ============================================================================
// Add stock modal (record purchase)
// ============================================================================

function AddStockModal({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: () => void;
}) {
  const [locations, setLocations] = useState<ChemicalInventoryLocation[]>([]);
  const [chemicals, setChemicals] = useState<any[]>([]);
  const [form, setForm] = useState({
    location_id: '',
    chemical_id: '',
    quantity: '',
    unit: 'L',
    supplier: '',
    purchase_date: '',
    batch_number: '',
    expiry_date: '',
    unit_cost: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    sprayService.getInventoryLocations().then(res => {
      if (res.success && res.data) setLocations((res.data as ChemicalInventoryLocation[]).filter(l => l.is_active));
    });
    sprayService.searchChemicals('', 1, 100).then(res => {
      if (res.success) setChemicals(res.data || []);
    });
  }, []);

  const f = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleAdd = async () => {
    if (!form.location_id || !form.chemical_id || !form.quantity) {
      setError('Location, chemical, and quantity are required');
      return;
    }
    setSaving(true);
    setError(null);
    const res = await sprayService.recordPurchase({
      location_id: Number(form.location_id),
      chemical_id: Number(form.chemical_id),
      quantity: parseFloat(form.quantity),
      unit: form.unit,
      supplier: form.supplier || undefined,
      purchase_date: form.purchase_date || undefined,
      batch_number: form.batch_number || undefined,
      expiry_date: form.expiry_date || undefined,
      unit_cost: form.unit_cost ? parseFloat(form.unit_cost) : undefined,
      notes: form.notes || undefined,
    });
    if (res.success) {
      onAdded();
    } else {
      setError((res as any).error?.message || 'Failed to record purchase');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full my-4">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-base font-semibold text-neutral-800 flex items-center gap-2">
            <ArrowDownCircle className="h-5 w-5 text-green-600" />
            Record Purchase (Stock In)
          </h3>
        </div>
        <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Location *</label>
            <select value={form.location_id} onChange={f('location_id')}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Select location...</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>
                  {loc.farm_name ? `${loc.farm_name} — ${loc.name}` : loc.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Chemical *</label>
            <select value={form.chemical_id} onChange={f('chemical_id')}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Select chemical...</option>
              {chemicals.map((c: any) => <option key={c.id} value={c.id}>{c.product_name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Quantity *</label>
              <input type="number" min="0" step="0.001" value={form.quantity} onChange={f('quantity')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Unit</label>
              <select value={form.unit} onChange={f('unit')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                <option value="L">L</option>
                <option value="mL">mL</option>
                <option value="kg">kg</option>
                <option value="g">g</option>
                <option value="unit">unit</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Supplier</label>
              <input type="text" value={form.supplier} onChange={f('supplier')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Unit cost ($)</label>
              <input type="number" min="0" step="0.01" value={form.unit_cost} onChange={f('unit_cost')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Purchase date</label>
              <input type="date" value={form.purchase_date} onChange={f('purchase_date')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Expiry date</label>
              <input type="date" value={form.expiry_date} onChange={f('expiry_date')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Batch number</label>
            <input type="text" value={form.batch_number} onChange={f('batch_number')}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Notes</label>
            <textarea value={form.notes} onChange={f('notes')} rows={2}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm resize-none" />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleAdd} disabled={saving}>
            {saving ? 'Saving...' : 'Record Purchase'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Adjust stock modal (stocktake correction)
// ============================================================================

function AdjustStockModal({
  row,
  onClose,
  onSaved,
}: {
  row: InventoryStockLevel;
  onClose: () => void;
  onSaved: () => void;
}) {
  const systemStock = parseFloat(String(row.current_stock));
  const [counted, setCounted] = useState(systemStock.toFixed(2));
  const [notes, setNotes] = useState('Stocktake correction');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const countedNum = parseFloat(counted);
  const hasValidCount = counted.trim() !== '' && !isNaN(countedNum) && countedNum >= 0;
  const delta = hasValidCount ? parseFloat((countedNum - systemStock).toFixed(4)) : 0;
  const noChange = hasValidCount && delta === 0;

  const handleSave = async () => {
    if (!hasValidCount) {
      setError('Enter a valid counted quantity');
      return;
    }
    if (noChange) {
      setError('Counted quantity matches current stock — nothing to adjust');
      return;
    }
    setSaving(true);
    setError(null);
    const res = await sprayService.recordAdjustment({
      location_id: row.location_id,
      chemical_id: row.chemical_id,
      counted_quantity: countedNum,
      unit: row.unit,
      notes: notes.trim() || undefined,
    });
    if (res.success) {
      onSaved();
    } else {
      setError((res as any).error?.message || 'Failed to record adjustment');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full my-4">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-base font-semibold text-neutral-800 flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-blue-600" />
            Adjust Stock (Stocktake)
          </h3>
          <p className="text-xs text-neutral-500 mt-1">
            {row.product_name}
            {row.trade_name && <span className="text-neutral-400"> ({row.trade_name})</span>}
            {' · '}{row.location_name}
          </p>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="flex items-center justify-between bg-neutral-50 rounded-lg px-4 py-3">
            <span className="text-sm text-neutral-600">System stock</span>
            <span className="text-sm font-semibold text-neutral-800">{systemStock.toFixed(2)} {row.unit}</span>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Counted quantity *</label>
            <div className="flex items-center gap-2">
              <input
                type="number" min="0" step="0.001" value={counted}
                onChange={e => setCounted(e.target.value)}
                autoFocus
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
              />
              <span className="text-sm text-neutral-500 w-10">{row.unit}</span>
            </div>
          </div>
          <div className="flex items-center justify-between px-1">
            <span className="text-sm text-neutral-600">Adjustment</span>
            <span className={`text-sm font-semibold ${!hasValidCount || noChange ? 'text-neutral-400' : delta > 0 ? 'text-green-700' : 'text-red-600'}`}>
              {hasValidCount ? `${delta > 0 ? '+' : ''}${delta} ${row.unit}` : '—'}
            </span>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Reason / notes</label>
            <input
              type="text" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Stocktake correction"
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !hasValidCount || noChange}>
            {saving ? 'Saving...' : 'Save adjustment'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Transaction type badge
// ============================================================================

function TxTypeBadge({ type }: { type: string }) {
  const cfg: Record<string, { label: string; className: string }> = {
    purchase:     { label: 'Purchase',    className: 'bg-green-100 text-green-700' },
    spray_usage:  { label: 'Spray Usage', className: 'bg-blue-100 text-blue-700'  },
    adjustment:   { label: 'Adjustment',  className: 'bg-neutral-100 text-neutral-700' },
    disposal:     { label: 'Disposal',    className: 'bg-red-100 text-red-700'    },
  };
  const c = cfg[type] || { label: type, className: 'bg-neutral-100 text-neutral-600' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${c.className}`}>
      {c.label}
    </span>
  );
}

// ============================================================================
// Main page
// ============================================================================

function InventoryDashboard() {
  const timezone = useOrgTimezone();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'stock' | 'history'>('stock');
  const [locations, setLocations] = useState<ChemicalInventoryLocation[]>([]);
  const [stockLevels, setStockLevels] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showAddStock, setShowAddStock] = useState(false);
  const [adjustRow, setAdjustRow] = useState<InventoryStockLevel | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<number | ''>('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const HISTORY_LIMIT = 25;

  const loadLocations = async () => {
    const res = await sprayService.getInventoryLocations();
    if (res.success && res.data) setLocations(res.data as ChemicalInventoryLocation[]);
  };

  const loadStockLevels = async () => {
    const res = await sprayService.getStockLevels(selectedLocation !== '' ? Number(selectedLocation) : undefined);
    if (res.success && res.data) setStockLevels(res.data as any[]);
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    const res = await sprayService.getTransactionHistory({
      location_id: selectedLocation !== '' ? Number(selectedLocation) : undefined,
      page: historyPage,
      limit: HISTORY_LIMIT,
    });
    if (res.success) {
      setTransactions(res.data || []);
      setHistoryTotal(res.total || 0);
    }
    setLoadingHistory(false);
  };

  useEffect(() => {
    setSelectedLocation('');
    loadLocations();
  }, [user?.organisation_id]);
  useEffect(() => { loadStockLevels(); }, [selectedLocation]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === 'history') loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedLocation, historyPage]);

  return (
    <>
      <PageHeader
        title="Chemical Inventory"
        subtitle="Track stock levels and usage across storage locations"
        icon={<Package className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="primary" size="sm" onClick={() => setShowAddStock(true)}>
            <Plus className="h-4 w-4" />
            Record Purchase
          </Button>
        }
      />
      <SprayNav />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Tabs
          tabs={[
            { key: 'stock', label: 'Stock Levels' },
            { key: 'history', label: 'Transaction History' },
          ]}
          active={activeTab}
          onChange={setActiveTab}
          className="mb-4"
        />

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-neutral-400" />
            <span className="text-sm text-neutral-600">Location:</span>
          </div>
          <select
            value={selectedLocation}
            onChange={e => setSelectedLocation(e.target.value ? Number(e.target.value) : '')}
            className="border border-neutral-300 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="">All locations</option>
            {locations.map(loc => (
              <option key={loc.id} value={loc.id}>
                {loc.farm_name ? `${loc.farm_name} — ${loc.name}` : loc.name}
              </option>
            ))}
          </select>
        </div>

        {/* Content */}
        {activeTab === 'stock' ? (
          <InventoryStockTable
            stock={stockLevels}
            onRecordPurchase={() => setShowAddStock(true)}
            onAdjust={setAdjustRow}
          />
        ) : (
          <div>
            {loadingHistory ? (
              <div className="space-y-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-14 bg-neutral-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12 text-neutral-400">
                <Package className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No transactions recorded yet</p>
              </div>
            ) : (
              <>
                <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-neutral-50 border-b border-neutral-200">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Date</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Type</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Chemical</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Location</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">Qty</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide hidden md:table-cell">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {transactions.map(tx => (
                        <tr key={tx.id} className="hover:bg-neutral-50">
                          <td className="px-4 py-3 text-neutral-600">
                            {formatDateInTz(tx.recorded_at, timezone)}
                          </td>
                          <td className="px-4 py-3">
                            <TxTypeBadge type={tx.transaction_type} />
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-medium text-neutral-800">{tx.product_name}</span>
                            {tx.trade_name && <span className="text-neutral-400 text-xs ml-1">{tx.trade_name}</span>}
                          </td>
                          <td className="px-4 py-3 text-neutral-600">{tx.location_name}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-semibold ${toNumOr(tx.quantity) > 0 ? 'text-green-700' : 'text-red-600'}`}>
                              {toNumOr(tx.quantity) > 0 ? '+' : ''}{toNumOr(tx.quantity)} {tx.unit}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-neutral-400 text-xs hidden md:table-cell truncate max-w-xs">
                            {tx.supplier || tx.notes || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {historyTotal > HISTORY_LIMIT && (
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <Button variant="outline" size="sm" disabled={historyPage === 1}
                      onClick={() => setHistoryPage(p => p - 1)}>Previous</Button>
                    <span className="text-sm text-neutral-500">
                      Page {historyPage} of {Math.ceil(historyTotal / HISTORY_LIMIT)}
                    </span>
                    <Button variant="outline" size="sm" disabled={historyPage * HISTORY_LIMIT >= historyTotal}
                      onClick={() => setHistoryPage(p => p + 1)}>Next</Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {showAddStock && (
          <AddStockModal
            onClose={() => setShowAddStock(false)}
            onAdded={() => {
              setShowAddStock(false);
              loadStockLevels();
              if (activeTab === 'history') loadHistory();
            }}
          />
        )}

        {adjustRow && (
          <AdjustStockModal
            row={adjustRow}
            onClose={() => setAdjustRow(null)}
            onSaved={() => {
              setAdjustRow(null);
              loadStockLevels();
              if (activeTab === 'history') loadHistory();
            }}
          />
        )}
      </div>
    </>
  );
}

export default InventoryDashboard;
